import CryptoJS from 'crypto-js';
import { defineStore } from 'pinia';
import axios from 'axios';
import type { JWTPayload } from 'jose';
import * as jose from 'jose';

type TokensByCodeParams = {
    clientId: string;
    redirectUrl: string;
    code: string;
    codeVerifier: string;
};

type TokensByRefreshTokenParams = {
    clientId: string;
    refreshToken: string;
};

type Tokens = {
    accessToken: string;
    idToken?: string;
    refreshToken?: string;
};

type TokensAndDecoded = {
    tokens: Tokens;
    decoded: JWTPayload;
};

export type Subscription = {
    type: string;
    value: string;
};

export type UserType = 'pupil' | 'teacher' | 'admin' | 'staff';

const STORAGE_KEY_TOKENS = 'giglets.tokens';
const REFRESH_WINDOW_SECONDS = 60;

export const useAuth = defineStore(
    'auth',
    () => {
        const { authClientId } = useEnv();
        const tokensAndDecoded = ref<TokensAndDecoded | null>(null);
        const userType = ref<UserType>();

        /**
         * @param expiresAt unix timestamp in seconds when the token expires
         * @returns number of milliseconds until the token should be refreshed
         */
        const calculateNextRefreshIn = (expiresAt: number): number => {
            const currentTimestampInSecond = Math.round(Date.now() / 1000);

            const refreshInSeconds = expiresAt - currentTimestampInSecond - REFRESH_WINDOW_SECONDS;
            if (refreshInSeconds < 0) {
                return 0;
            }

            return refreshInSeconds * 1000;
        };

        const refreshAccessToken = async () => {
            if (!tokensAndDecoded.value || !tokensAndDecoded.value.tokens.refreshToken) {
                return;
            }

            console.log('Refreshing access token');
            const newTokens = await tokensByRefreshToken({
                refreshToken: tokensAndDecoded.value.tokens.refreshToken,
                clientId: authClientId,
            });
            console.log('Setting tokens from refresh', newTokens.decoded);
            setTokens(newTokens);
        };

        const setTokens = (newTokens: TokensAndDecoded) => {
            const expiresAtSeconds = newTokens.decoded.exp || 0;
            if (expiresAtSeconds < Date.now() / 1000) {
                console.log('Token has expired');
                return;
            }
            if (newTokens.tokens.refreshToken) {
                const refreshInMs = calculateNextRefreshIn(expiresAtSeconds);
                if (newTokens.tokens.refreshToken && refreshInMs > 0) {
                    console.log('Setting refresh timeout to', refreshInMs, 'ms');
                    setTimeout(refreshAccessToken, refreshInMs);
                }
            }

            // Store in local storage
            localStorage.setItem(STORAGE_KEY_TOKENS, JSON.stringify(newTokens.tokens));

            // Store in cookie
            const expires = new Date(expiresAtSeconds * 1000);
            // Safari does not allow secure cookies on http/localhost
            const secure = window.location.protocol === 'https:' ? 'secure;' : '';
            document.cookie = `giglets-token=${newTokens.tokens.accessToken};${secure}path=/;expires=${expires.toUTCString()}`;

            (window as any).API_TOKEN = newTokens.tokens.accessToken;

            tokensAndDecoded.value = newTokens;
        };

        // Load tokens from local storage on creation of the store
        const localTokens = localStorage.getItem(STORAGE_KEY_TOKENS);
        if (localTokens) {
            console.log('Loaded tokens from localstorage');
            const tokens = JSON.parse(localTokens) as Tokens;
            if (tokens.accessToken) {
                const decoded = jose.decodeJwt(tokens.accessToken);
                console.log('setting tokens from localstorage', decoded);
                setTokens({ tokens, decoded });
            }
        }

        const createCodeChallenge = () => {
            const codeVerifier = CryptoJS.lib.WordArray.random(32)
                .toString(CryptoJS.enc.Base64)
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .replace(/=/g, '');
            const codeChallenge = CryptoJS.SHA256(codeVerifier)
                .toString(CryptoJS.enc.Base64)
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .replace(/=/g, '');
            return {
                codeVerifier,
                codeChallenge,
            };
        };

        const createIltAuthUrl = (redirectUri: string, reauthenticate?: string) => {
            const { codeChallenge, codeVerifier } = createCodeChallenge();
            localStorage.setItem('codeVerifier', codeVerifier);

            const searchParams = new URLSearchParams();
            searchParams.set('client_id', authClientId);
            searchParams.set('redirect_uri', redirectUri);
            searchParams.set('response_type', 'code');
            searchParams.set('scope', 'openid profile');
            searchParams.set('code_challenge', codeChallenge);
            searchParams.set('code_challenge_method', 'S256');
            if (reauthenticate) {
                searchParams.set('reauthenticate', reauthenticate);
            }

            return `${authUrl.value}/v1/authorize?${searchParams.toString()}`;
        };

        const authUrl = computed(() => {
            const match = window.location.hostname.match(/(.*?)(-[^.]*)?[.](.*)/);
            if (!match) {
                throw new Error('Invalid host');
            }
            const stage = match[2] || '';
            const domain = match[3];
            if (
                (stage.includes('-staging') || domain === 'localhost') &&
                import.meta.env.VITE_ENVIRONMENT !== 'production'
            ) {
                return `https://auth-dev.ilteducation.co.uk`;
            }
            return `https://auth.ilteducation.co.uk`;
        });

        const getTokens = async (params: URLSearchParams): Promise<TokensAndDecoded> => {
            const response = await axios.post(`${authUrl.value}/v2/token`, params, {
                headers: { 'Content-type': 'application/x-www-form-urlencoded' },
            });
            return {
                tokens: {
                    accessToken: response.data.access_token as string,
                    idToken: response.data.id_token as string,
                    refreshToken: response.data.refresh_token as string,
                },
                decoded: jose.decodeJwt(response.data.access_token),
            };
        };

        const tokensByCode = (params: TokensByCodeParams): Promise<TokensAndDecoded> => {
            const tokenParams = new URLSearchParams();

            tokenParams.set('grant_type', 'authorization_code');
            tokenParams.set('code', params.code);
            tokenParams.set('client_id', params.clientId);
            tokenParams.set('redirect_uri', params.redirectUrl);
            tokenParams.set('code_verifier', params.codeVerifier);

            return getTokens(tokenParams);
        };

        const tokensByRefreshToken = (params: TokensByRefreshTokenParams): Promise<TokensAndDecoded> => {
            const refreshParams = new URLSearchParams();

            refreshParams.set('grant_type', 'refresh_token');
            refreshParams.set('refresh_token', params.refreshToken);
            refreshParams.set('client_id', params.clientId);

            return getTokens(refreshParams);
        };

        const loginWithCode = async (code: string, redirectUrl: string): Promise<void> => {
            const codeVerifier = localStorage.getItem('codeVerifier')!;
            const tokens = await tokensByCode({
                clientId: authClientId,
                redirectUrl,
                code,
                codeVerifier,
            });
            console.log('Setting tokens from code', tokens.decoded);
            setTokens(tokens);
        };

        const loginWithToken = async (type: UserType, token?: string) => {
            const res = await $fetch<{ accessToken: string; expires: string }>('/api/login', {
                method: 'POST',
                body: {
                    type,
                    token,
                },
            });
            const { accessToken } = res;
            const decoded = jose.decodeJwt(accessToken);

            console.log('Setting tokens from password', decoded);
            setTokens({ tokens: { accessToken }, decoded });
        };

        const isLoggedIn = computed(() => {
            if (!tokensAndDecoded.value) {
                return false;
            }

            const { exp: expiryInSeconds } = tokensAndDecoded.value.decoded;
            if (!expiryInSeconds) {
                return false;
            }
            const nowInSeconds = Date.now() / 1000;

            return nowInSeconds < expiryInSeconds;
        });

        // Clear tokens from local storage and cookies, but do not clear the store state as that would trigger a reactivity updates
        const clearTokens = () => {
            document.cookie = `giglets-token=;path=/;expires=${new Date(0).toUTCString()}`;
            localStorage.removeItem(STORAGE_KEY_TOKENS);
            userType.value = undefined;
        };

        const logout = () => {
            clearTokens();
            tokensAndDecoded.value = null;
        };

        const scopes = computed(() => ((tokensAndDecoded.value?.decoded.scope as string) || '').split(' '));

        return {
            createIltAuthUrl,
            loginWithCode,
            loginWithToken,
            userType,
            isLoggedIn,
            clearTokens,
            logout,
            sub: computed(() => tokensAndDecoded.value?.decoded.sub),
            scopes,
            accessToken: computed(() => tokensAndDecoded.value?.tokens.accessToken),
            subscriptions: computed(() => tokensAndDecoded.value?.decoded.subscriptions as Subscription[] | undefined),
        };
    },
    { persist: { pick: ['userType'] } },
);
